import debounce from 'lodash/debounce';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';

import { IdentityFieldValues } from '@/pages/UserAccount/Profile';

interface Props {
  methods: ReturnType<typeof useForm<IdentityFieldValues>>;
  triggerReinitCount: number;
}

export const useUpdateIdentityCard = ({ methods, triggerReinitCount }: Props): void => {
  const prevValues = useRef<Partial<IdentityFieldValues>>({
    citizenship: null,
    passportDocTypeCode: undefined,
    withoutCitizenship: null,
    passport: null,
    documents: {
      passport: {
        info: {},
        type: null,
      },
    },
  });
  const skipWatch = useRef(false);

  const updateFormFields = (fields: Partial<IdentityFieldValues>, shouldSkipWatch = true): void => {
    skipWatch.current = shouldSkipWatch;
    Object.entries(fields).forEach(([field, value]) => {
      methods.setValue(field as keyof IdentityFieldValues, value, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    });
  };

  useEffect(() => {
    prevValues.current = methods.getValues();
    if (triggerReinitCount === 0) return;
    skipWatch.current = true;
  }, [triggerReinitCount, methods]);

  const debouncedWatcher = debounce(async (values) => {
    const { withoutCitizenship, citizenship, passportDocTypeCode, passport, documents } = values;

    if (passport?.code !== prevValues.current.passport?.code) {
      updateFormFields({
        passportDocTypeCode: passport?.code,
      });

      Object.keys(documents.passport.info).forEach((key) => {
        methods.setValue(`documents.passport.info.${key}` as keyof IdentityFieldValues, '', {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      });
      methods.setValue('documents.passport.type', passportDocTypeCode, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }

    if (withoutCitizenship?.id !== prevValues.current.withoutCitizenship?.id) {
      updateFormFields({
        citizenship: null,
        passport: null,
        passportDocTypeCode: undefined,
      } as Partial<IdentityFieldValues>);
    }

    if (citizenship?.id !== prevValues.current.citizenship?.id) {
      updateFormFields({
        passport: null,
        passportDocTypeCode: undefined,
      } as Partial<IdentityFieldValues>);
    }

    prevValues.current = {
      passport,
      passportDocTypeCode,
      withoutCitizenship,
      citizenship,
      documents,
    } as Partial<IdentityFieldValues>;
  }, 0);

  useEffect(() => {
    const subscription = methods.watch((values) => debouncedWatcher(values));

    return () => subscription.unsubscribe();
  }, [methods]);
};
